import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Heading3, Save, Loader2, Check, Eye, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BriefEditorProps {
  content: string;
  onSave: (markdown: string) => Promise<void>;
  readOnly?: boolean;
}

type SaveStatus = 'idle' | 'saving' | 'saved';

export function BriefEditor({ content, onSave, readOnly = false }: BriefEditorProps) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestContentRef = useRef(content);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Markdown.configure({ html: false }),
    ],
    content,
    editable: !readOnly && !isPreview,
    onUpdate: ({ editor }) => {
      const md = editor.storage.markdown.getMarkdown();
      latestContentRef.current = md;
      setIsDirty(true);
      setSaveStatus('idle');

      // Auto-save after 2 seconds of inactivity
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        handleSave(md);
      }, 2000);
    },
  });

  // Update editable state when readOnly or isPreview changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly && !isPreview);
    }
  }, [editor, readOnly, isPreview]);

  const handleSave = useCallback(async (markdown: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaveStatus('saving');
    try {
      await onSave(markdown);
      setIsDirty(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
    }
  }, [onSave]);

  // Save on blur
  useEffect(() => {
    if (!editor) return;
    const handleBlur = () => {
      if (isDirty) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        handleSave(latestContentRef.current);
      }
    };
    editor.on('blur', handleBlur);
    return () => {
      editor.off('blur', handleBlur);
    };
  }, [editor, isDirty, handleSave]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleTogglePreview = useCallback(() => {
    if (!isPreview && isDirty) {
      // Auto-save before entering preview
      if (debounceRef.current) clearTimeout(debounceRef.current);
      handleSave(latestContentRef.current);
    }
    setIsPreview(prev => !prev);
  }, [isPreview, isDirty, handleSave]);

  if (!editor) return null;

  return (
    <div className="border border-border rounded-md overflow-hidden flex flex-col h-full">
      {!readOnly && (
        <div className="flex items-center gap-0.5 p-1 border-b border-border bg-muted/30 flex-shrink-0">
          {!isPreview && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('h-7 w-7 p-0', editor.isActive('heading', { level: 1 }) && 'bg-muted')}
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              >
                <Heading1 className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('h-7 w-7 p-0', editor.isActive('heading', { level: 2 }) && 'bg-muted')}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              >
                <Heading2 className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('h-7 w-7 p-0', editor.isActive('heading', { level: 3 }) && 'bg-muted')}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              >
                <Heading3 className="w-3.5 h-3.5" />
              </Button>
              <div className="w-px h-4 bg-border mx-1" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('h-7 w-7 p-0', editor.isActive('bold') && 'bg-muted')}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <Bold className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('h-7 w-7 p-0', editor.isActive('italic') && 'bg-muted')}
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                <Italic className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('h-7 w-7 p-0', editor.isActive('bulletList') && 'bg-muted')}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                <List className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('h-7 w-7 p-0', editor.isActive('orderedList') && 'bg-muted')}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              >
                <ListOrdered className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
          <div className="ml-auto flex items-center gap-1.5 pr-1">
            {saveStatus === 'saving' && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                <Check className="w-3 h-3" />
                Saved
              </span>
            )}
            {isDirty && saveStatus === 'idle' && !isPreview && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => handleSave(latestContentRef.current)}
              >
                <Save className="w-3 h-3 mr-1" />
                Save
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleTogglePreview}
            >
              {isPreview ? (
                <>
                  <Pencil className="w-3 h-3 mr-1" />
                  Edit
                </>
              ) : (
                <>
                  <Eye className="w-3 h-3 mr-1" />
                  Preview
                </>
              )}
            </Button>
          </div>
        </div>
      )}
      <EditorContent
        editor={editor}
        className="prose prose-sm dark:prose-invert max-w-none p-4 flex-1 overflow-auto focus-within:outline-none [&_.tiptap]:outline-none [&_.tiptap]:min-h-[400px]"
      />
    </div>
  );
}
